import type { FC } from 'react';
import { useMemo, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { CASH } from '@makemydeal/dr-platform-types';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import { Spinner } from '@interstate/components/Spinner';
import { Alert } from '@interstate/components/Alert';
import { Button } from '@interstate/components/Button';
import * as programActionCreators from '@makemydeal/dr-dash-store/actions/programActionCreators';
import * as programsSelectors from '@makemydeal/dr-dash-store/selectors/programsSelectors';
import { dealLockHooks } from '@makemydeal/dr-dash-components';
import { LoadingIndicatorWrapper, ListWithOverlayWrapper, RecalcOverlay } from './styles';
import { IncentivesTableView } from './IncentivesTableView';
import type { IncentiveDisabledReason } from './IncentivesTableView';
import {
    ERROR_MESSAGE,
    NO_DATA_EXIST_MESSAGE,
    PROGRAM_LOSS_MESSAGE,
    ZERO_AMOUNT_MESSAGE,
    buildExclusionMessage
} from '../../constants';
import { Typography } from '@interstate/components/Typography';

type NestedIncentive = {
    incentiveId: number;
    cash: number;
    programName: string;
    displayType: string;
    selected: boolean;
    isExcluded: boolean;
    blockingIncentiveName?: string;
    isProgramLossDisabled?: boolean;
    isOverridden?: boolean;
    description: string;
    [key: string]: any;
};

const buildDisabledReason = (
    incentive: NestedIncentive,
    blockedFromSelecting: boolean,
    blockedByZeroAmount: boolean
): IncentiveDisabledReason | undefined => {
    if (blockedFromSelecting) {
        return { kind: 'exclusion', message: buildExclusionMessage(incentive.blockingIncentiveName) };
    }
    if (blockedByZeroAmount) {
        return { kind: 'zero-amount', message: ZERO_AMOUNT_MESSAGE };
    }
    if (incentive.isProgramLossDisabled) {
        return { kind: 'program-loss', message: PROGRAM_LOSS_MESSAGE };
    }
    return undefined;
};

type NestedIncentivesTableProps = {
    nestedIncentivesTableData: NestedIncentive[];
    isError: boolean;
    isLoading: boolean;
    isEmpty: boolean;
    isProgramsLoading?: boolean;
    isApplying?: boolean;
};

export const NestedIncentivesTable: FC<NestedIncentivesTableProps> = ({
    nestedIncentivesTableData,
    isError,
    isLoading,
    isEmpty,
    isProgramsLoading = false,
    isApplying = false
}) => {
    const dispatch = useDispatch();
    const isLocked = dealLockHooks.useIsDealLocked();
    const isProgramIncentivesRestorationEnabled = useSelector(featureToggleSelectors.isProgramIncentivesRestorationEnabled);
    const isCalculatingPayment = useSelector(offerReduxSelectors.isCalculatingPayment);
    const isApplyingProgram = useSelector(programsSelectors.isApplyingProgram);
    const lockForRecalc = isProgramIncentivesRestorationEnabled && isCalculatingPayment;
    const offerType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const isCashFixesEnabled = useSelector(featureToggleSelectors.isCashDealIncentivesFixesEnabled);
    // Cash has no lender program to identify, so Programs # has no real value for cash - show the
    // literal "CASH" label instead of a blank cell, rather than dropping the column.
    const showCashProgramLabel = offerType === CASH && isCashFixesEnabled;

    const handleCheckboxChange = useCallback(
        (incentive: NestedIncentive, checked: boolean) => {
            dispatch(programActionCreators.toggleSelectedIncentiveCompactUI(incentive, checked, true));
        },
        [dispatch]
    );

    const lastNonEmptyDataRef = useRef<NestedIncentive[]>([]);
    if (nestedIncentivesTableData.length > 0) {
        lastNonEmptyDataRef.current = nestedIncentivesTableData;
    }
    const isPendingResponse = isLoading || isProgramsLoading || isApplying;
    const effectiveData =
        nestedIncentivesTableData.length === 0 && isPendingResponse ? lastNonEmptyDataRef.current : nestedIncentivesTableData;

    const rows = useMemo(
        () => (showCashProgramLabel ? effectiveData.map((incentive) => ({ ...incentive, programId: 'CASH' })) : effectiveData),
        [effectiveData, showCashProgramLabel]
    );

    const getDisabledReason = useCallback(
        (incentive: NestedIncentive) => {
            const isZeroAmount = isProgramIncentivesRestorationEnabled && incentive.cash === 0;
            return buildDisabledReason(incentive, incentive.isExcluded && !incentive.selected, isZeroAmount && !incentive.selected);
        },
        [isProgramIncentivesRestorationEnabled]
    );

    const isCheckboxDisabled = useCallback(
        (_incentive: NestedIncentive, disabledReason: IncentiveDisabledReason | undefined) =>
            Boolean(disabledReason) || isLocked || isProgramsLoading || lockForRecalc,
        [isLocked, isProgramsLoading, lockForRecalc]
    );

    const showLoadingOverlay = isPendingResponse;

    if (showLoadingOverlay && rows.length === 0) {
        return (
            <LoadingIndicatorWrapper>
                <Spinner size={50} />
            </LoadingIndicatorWrapper>
        );
    }

    if (isError) {
        return (
            <Alert type="error">
                <Typography variant="label-md">{ERROR_MESSAGE}</Typography>
            </Alert>
        );
    }

    if (isEmpty && rows.length === 0) {
        return (
            <Alert type="info">
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <Typography variant="label-md">{NO_DATA_EXIST_MESSAGE}</Typography>
                    <Button
                        data-testid="reload-incentives-button"
                        size="sm"
                        buttonStyle="secondary"
                        disabled={isLocked || lockForRecalc || isProgramsLoading || isCalculatingPayment || isApplyingProgram}
                        onClick={() => dispatch(programActionCreators.applyCurrentSelectedProgram(undefined, undefined, true))}
                    >
                        Reload incentives
                    </Button>
                </div>
            </Alert>
        );
    }

    const table = (
        <IncentivesTableView
            rows={rows}
            getDisabledReason={getDisabledReason}
            isCheckboxDisabled={isCheckboxDisabled}
            onToggle={handleCheckboxChange}
        />
    );

    if (!showLoadingOverlay) {
        return table;
    }

    return (
        <ListWithOverlayWrapper>
            <RecalcOverlay data-testid="incentives-recalculating-overlay">
                <Spinner size={50} />
            </RecalcOverlay>
            {table}
        </ListWithOverlayWrapper>
    );
};
